import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const STEPS = [
  { icon: '🗺️', key: 'loading_destino',      fallback: 'Buscando tu destino...' },
  { icon: '🏛️', key: 'loading_actividades',   fallback: 'Explorando actividades...' },
  { icon: '📅', key: 'loading_organizando',   fallback: 'Organizando por días...' },
  { icon: '🧭', key: 'loading_rutas',         fallback: 'Calculando rutas óptimas...' },
  { icon: '✨', key: 'loading_experiencias',  fallback: 'Seleccionando experiencias únicas...' },
  { icon: '💰', key: 'loading_presupuesto',   fallback: 'Ajustando presupuesto...' },
  { icon: '🎒', key: 'loading_casi_listo',    fallback: '¡Casi listo! Preparando tu itinerario...' },
];

function LoadingOverlayAnimada({ visible }) {
  const { t } = useTranslation();
  const steps = useMemo(() => STEPS.map(s => ({ ...s, text: t(s.key, s.fallback) })), [t]);

  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [progress, setProgress] = useState(0);

  // Advance steps
  useEffect(() => {
    if (!visible) return;
    setIndex(0);
    setFade(true);
    setProgress(0);
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIndex(prev => {
          const next = prev + 1 < steps.length ? prev + 1 : prev;
          setProgress(Math.round((next / (steps.length - 1)) * 99));
          return next;
        });
        setFade(true);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, [visible, steps.length]);

  if (!visible) return null;

  const current = steps[index];

  return (
    <>
      <style>{`
        @keyframes pl-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes pl-pulse-ring {
          0%   { transform: scale(0.85); opacity: 0.7; }
          50%  { transform: scale(1.15); opacity: 0.15; }
          100% { transform: scale(0.85); opacity: 0.7; }
        }
        @keyframes pl-float {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-8px); }
        }
        @keyframes pl-fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pl-shimmer {
          0%   { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        .pl-step-fade {
          animation: pl-fade-in 0.35s ease forwards;
        }
        .pl-step-hidden {
          opacity: 0;
        }
      `}</style>

      <div style={overlayStyle}>
        <div style={cardStyle}>

          {/* Animated logo ring */}
          <div style={ringWrapStyle}>
            {/* Pulsing background ring */}
            <div style={pulseRingStyle} />
            {/* Spinning gradient ring */}
            <div style={spinRingStyle} />
            {/* Center icon */}
            <div style={iconCircleStyle}>
              <span style={{ fontSize: 30, lineHeight: 1 }}>{current.icon}</span>
            </div>
          </div>

          {/* Title */}
          <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
            <span style={brandStyle}>Planiaro</span>
            <p style={subtitleStyle}>{t('loading_generando', 'Generando tu itinerario con IA')}</p>
          </div>

          {/* Progress bar */}
          <div style={progressTrackStyle}>
            <div style={{ ...progressFillStyle, width: `${progress}%` }} />
          </div>
          <div style={progressLabelStyle}>{progress}%</div>

          {/* Step text */}
          <div
            className={fade ? 'pl-step-fade' : 'pl-step-hidden'}
            style={stepTextStyle}
          >
            {current.text}
          </div>

          {/* Step dots */}
          <div style={dotsStyle}>
            {steps.map((_, i) => (
              <div
                key={i}
                style={{
                  ...dotStyle,
                  background: i <= index
                    ? 'linear-gradient(135deg, #6B30EA, #A66CFF)'
                    : '#E9E7FB',
                  transform: i === index ? 'scale(1.3)' : 'scale(1)',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </div>

        </div>
      </div>
    </>
  );
}

export default LoadingOverlayAnimada;

/* ── Styles ─────────────────────────────────────────────── */

const overlayStyle = {
  position: 'fixed',
  zIndex: 9999,
  top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(245, 240, 255, 0.96)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem',
};

const cardStyle = {
  background: '#fff',
  borderRadius: '1.75rem',
  border: '2px solid #6B30EA22',
  boxShadow: '0 20px 60px rgba(107,48,234,0.15)',
  padding: '2.5rem 2rem 2rem',
  width: '100%',
  maxWidth: 380,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1.1rem',
};

const ringWrapStyle = {
  position: 'relative',
  width: 100,
  height: 100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '0.25rem',
};

const pulseRingStyle = {
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  background: 'radial-gradient(circle, #A66CFF33 0%, transparent 70%)',
  animation: 'pl-pulse-ring 2s ease-in-out infinite',
};

const spinRingStyle = {
  position: 'absolute',
  inset: 4,
  borderRadius: '50%',
  border: '3px solid transparent',
  borderTopColor: '#6B30EA',
  borderRightColor: '#A66CFF',
  animation: 'pl-spin 1.1s linear infinite',
};

const iconCircleStyle = {
  width: 68,
  height: 68,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #F5F0FF, #EDE8FF)',
  border: '2px solid #6B30EA22',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  animation: 'pl-float 3s ease-in-out infinite',
  boxShadow: '0 4px 20px rgba(107,48,234,0.12)',
};

const brandStyle = {
  fontSize: 22,
  fontWeight: 800,
  background: 'linear-gradient(135deg, #6B30EA, #A66CFF)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  letterSpacing: '-0.5px',
};

const subtitleStyle = {
  fontSize: 13,
  color: '#888',
  margin: '2px 0 0',
  fontWeight: 500,
};

const progressTrackStyle = {
  width: '100%',
  height: 6,
  background: '#EDE8FF',
  borderRadius: 99,
  overflow: 'hidden',
};

const progressFillStyle = {
  height: '100%',
  background: 'linear-gradient(90deg, #6B30EA, #A66CFF)',
  borderRadius: 99,
  transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
};

const progressLabelStyle = {
  fontSize: 12,
  color: '#6B30EA',
  fontWeight: 700,
  marginTop: '-0.5rem',
  alignSelf: 'flex-end',
};

const stepTextStyle = {
  fontSize: 14,
  color: '#444',
  fontWeight: 500,
  textAlign: 'center',
  minHeight: 40,
  lineHeight: 1.5,
  padding: '0 0.5rem',
};

const dotsStyle = {
  display: 'flex',
  gap: 6,
  marginTop: '0.25rem',
};

const dotStyle = {
  width: 8,
  height: 8,
  borderRadius: '50%',
};
